import React from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { CoordinatePatient } from '~/types/coordinate';

const DEMO_TODAY = new Date('2026-05-01');

type WorkspaceTab = 'providers' | 'insurance' | 'family' | 'clinical' | 'tasks' | 'activity';
type InsightContext = 'global' | 'family' | 'insurance' | 'providers';

interface Insight {
  id: string;
  message: string;
  actionLabel?: string;
  actionTab?: WorkspaceTab;
  context: InsightContext;
  // 'requirement' renders as a system checklist note, not an AI-attributed insight
  kind?: 'ai' | 'requirement';
}

function computeInsights(patient: CoordinatePatient): Insight[] {
  const insights: Insight[] = [];

  // LOS over target — global, affects every decision on the case
  if (!['accepted', 'declined'].includes(patient.coordinationStatus)) {
    const [m, d, y] = patient.admitDate.split('/').map(Number);
    const admitDate = new Date(y, m - 1, d);
    const daysSinceAdmit = Math.floor((DEMO_TODAY.getTime() - admitDate.getTime()) / 86_400_000);
    if (daysSinceAdmit > patient.losTargetDays) {
      const over = daysSinceAdmit - patient.losTargetDays;
      insights.push({
        id: 'los-exceeded',
        context: 'global',
        message: `LOS alert: ${daysSinceAdmit} days admitted against a ${patient.losTargetDays}-day target — ${over} day${over !== 1 ? 's' : ''} over.`,
      });
    }
  }

  // Consent not documented — surfaces in Patient & Family tab
  if (
    ['confirmed', 'awaiting_response', 'negotiating', 'accepted'].includes(patient.coordinationStatus) &&
    !patient.patientContext?.consentDocumented
  ) {
    insights.push({
      id: 'consent-missing',
      context: 'family',
      message: 'Consent not yet documented — required before discharge can be confirmed.',
    });
  }

  const inReferralFlow = ['matched', 'confirming', 'confirmed', 'awaiting_response', 'negotiating', 'accepted'].includes(patient.coordinationStatus);

  if (patient.insuranceType === 'medicare') {
    // Medicare: no prior auth — but 3 documentation requirements must be met before billing
    const ins = patient.insuranceTrack;
    if (inReferralFlow && !ins?.homeboundDocumented) {
      insights.push({
        id: 'medicare-homebound',
        kind: 'requirement',
        context: 'insurance',
        message: 'Homebound status not confirmed — Medicare requires this in the clinical record before the agency can begin billing.',
      });
    }
    if (inReferralFlow && !ins?.f2fEncounterComplete) {
      insights.push({
        id: 'medicare-f2f',
        kind: 'requirement',
        context: 'insurance',
        message: 'Face-to-face encounter not confirmed — CMS requires a qualifying physician encounter within 90 days before or 30 days after start of care.',
      });
    }
    if (['accepted', 'awaiting_response'].includes(patient.coordinationStatus) &&
        (!ins?.physicianCertification485 || ins.physicianCertification485 === 'not_started')) {
      insights.push({
        id: 'medicare-485',
        kind: 'requirement',
        context: 'insurance',
        message: 'Plan of Care (485) not sent for physician signature — required before the agency can bill Medicare.',
      });
    }
  } else {
    // Commercial / Medicare Advantage / Medicaid: prior auth flow
    if (
      inReferralFlow &&
      (!patient.insuranceTrack || patient.insuranceTrack.status === 'not_checked')
    ) {
      insights.push({
        id: 'auth-not-checked',
        context: 'insurance',
        message: 'Authorization has not been verified. Most payers require prior auth before post-acute services begin — check before or alongside sending the referral.',
      });
    }

    if (patient.insuranceTrack?.status === 'pending' && patient.insuranceTrack.checkedAt) {
      const hoursWaiting = Math.floor(
        (DEMO_TODAY.getTime() - new Date(patient.insuranceTrack.checkedAt).getTime()) / 3_600_000
      );
      if (hoursWaiting >= 48) {
        insights.push({
          id: 'auth-pending-long',
          context: 'insurance',
          message: `Prior auth request has been pending for ${hoursWaiting} hours. Standard response is 24–72 hours — consider calling the payer directly to expedite.`,
        });
      }
    }
  }

  // Start of care not confirmed — surfaces in Provider Matching tab after acceptance
  if (patient.coordinationStatus === 'accepted' && !patient.startOfCareDate) {
    insights.push({
      id: 'soc-not-confirmed',
      context: 'providers',
      message: 'Start-of-care date has not been confirmed with the agency. Follow up before discharge to ensure a first visit is scheduled before the patient arrives home.',
    });
  }

  return insights;
}

interface Props {
  patient: CoordinatePatient;
  onSwitchTab: (tab: WorkspaceTab) => void;
  context: InsightContext;
}

const AgentInsightBanner: React.FC<Props> = ({ patient, onSwitchTab, context }) => {
  const insights = computeInsights(patient).filter(i => i.context === context);
  if (insights.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {insights.map(insight => {
        const isRequirement = insight.kind === 'requirement';
        return (
          <Box
            key={insight.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              border: '1px solid',
              borderColor: isRequirement ? 'rgba(237, 108, 2, 0.3)' : 'rgba(123, 31, 162, 0.25)',
              borderRadius: 1.5,
              bgcolor: isRequirement ? '#fffbf2' : '#f0eef8',
              px: 1.5,
              py: 1.25,
              mb: 1,
            }}
          >
            {isRequirement
              ? <ChecklistIcon sx={{ fontSize: 15, color: '#e65100', mt: 0.3, flexShrink: 0 }} />
              : <AutoAwesomeIcon sx={{ fontSize: 15, color: '#635f99', mt: 0.3, flexShrink: 0 }} />
            }
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1, minWidth: 0 }} flexWrap="wrap" useFlexGap spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: 'block', mb: 0.2, color: isRequirement ? '#e65100' : '#635f99' }}
                >
                  {isRequirement ? 'Documentation Required' : 'Coordinate AI'}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                  {insight.message}
                </Typography>
              </Box>
              {insight.actionLabel && insight.actionTab && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onSwitchTab(insight.actionTab!)}
                  sx={{
                    fontSize: '0.72rem',
                    flexShrink: 0,
                    color: isRequirement ? '#e65100' : '#635f99',
                    borderColor: isRequirement ? 'rgba(230, 81, 0, 0.4)' : 'rgba(123, 31, 162, 0.4)',
                    '&:hover': {
                      borderColor: isRequirement ? '#e65100' : '#635f99',
                      bgcolor: isRequirement ? 'rgba(230, 81, 0, 0.04)' : 'rgba(123, 31, 162, 0.04)',
                    },
                  }}
                >
                  {insight.actionLabel}
                </Button>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export { computeInsights };
export type { InsightContext };
export default AgentInsightBanner;
